import React, { useState, useEffect } from 'react';
import { Grid, Box, Divider, Typography } from '@mui/material';
import { FormSection, TextField, Select } from '../../../../design-system/components/forms';
import { PAYMENT_METHODS, PAYMENT_TERMS } from '../../../../constants/vendorConstants';
import { getDynamicCurrencyOptions } from '../../../../utils/dynamicCurrencyUtils';

const VendorPaymentForm = ({ formik }) => {
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);

  // Load currencies dynamically
  useEffect(() => {
    const loadCurrencies = async () => {
      setLoadingCurrencies(true);
      try {
        const currencyOptions = await getDynamicCurrencyOptions();
        setCurrencies(currencyOptions);
      } catch (error) {
        console.error('Failed to load currencies:', error);
        setCurrencies([]);
      } finally {
        setLoadingCurrencies(false);
      }
    };
    
    loadCurrencies();
  }, []);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    const keys = field.split('.');
    
    // Create a deep copy to avoid mutation
    const newValues = { ...formik.values };
    let current = newValues;
    
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    formik.setFieldValue(field, value);
  };

  return (
    <FormSection title="Payment Information">
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Select
            label="Preferred Payment Method"
            name="paymentInfo.preferredMethod"
            value={formik.values.paymentInfo.preferredMethod}
            onChange={(value) => formik.setFieldValue('paymentInfo.preferredMethod', value)}
            options={PAYMENT_METHODS}
            error={formik.touched.paymentInfo?.preferredMethod && Boolean(formik.errors.paymentInfo?.preferredMethod)}
            helperText={formik.touched.paymentInfo?.preferredMethod && formik.errors.paymentInfo?.preferredMethod}
            required
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Select
            label="Payment Terms"
            name="paymentInfo.paymentTermsDays"
            value={formik.values.paymentInfo.paymentTermsDays}
            onChange={(value) => formik.setFieldValue('paymentInfo.paymentTermsDays', value)}
            options={PAYMENT_TERMS}
            error={formik.touched.paymentInfo?.paymentTermsDays && Boolean(formik.errors.paymentInfo?.paymentTermsDays)}
            helperText={formik.touched.paymentInfo?.paymentTermsDays && formik.errors.paymentInfo?.paymentTermsDays}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Select
            label="Currency"
            name="currency"
            value={formik.values.currency}
            onChange={(value) => formik.setFieldValue('currency', value)}
            options={currencies}
          />
        </Grid>
        
        {formik.values.paymentInfo.preferredMethod === 'bank_transfer' && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Bank Details
                </Typography>
              </Divider>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Bank Name"
                name="paymentInfo.bankDetails.bankName"
                value={formik.values.paymentInfo.bankDetails.bankName}
                onChange={handleChange('paymentInfo.bankDetails.bankName')}
                onBlur={formik.handleBlur}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Account Name"
                name="paymentInfo.bankDetails.accountName"
                value={formik.values.paymentInfo.bankDetails.accountName}
                onChange={handleChange('paymentInfo.bankDetails.accountName')}
                onBlur={formik.handleBlur}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Account Number"
                name="paymentInfo.bankDetails.accountNumber"
                value={formik.values.paymentInfo.bankDetails.accountNumber}
                onChange={handleChange('paymentInfo.bankDetails.accountNumber')}
                onBlur={formik.handleBlur}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="IBAN"
                name="paymentInfo.bankDetails.iban"
                value={formik.values.paymentInfo.bankDetails.iban}
                onChange={handleChange('paymentInfo.bankDetails.iban')}
                onBlur={formik.handleBlur}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="SWIFT Code"
                name="paymentInfo.bankDetails.swiftCode"
                value={formik.values.paymentInfo.bankDetails.swiftCode}
                onChange={handleChange('paymentInfo.bankDetails.swiftCode')}
                onBlur={formik.handleBlur}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Branch Code"
                name="paymentInfo.bankDetails.branchCode"
                value={formik.values.paymentInfo.bankDetails.branchCode}
                onChange={handleChange('paymentInfo.bankDetails.branchCode')}
                onBlur={formik.handleBlur}
              />
            </Grid>
          </>
        )}
        
        {['instapay', 'paypal', 'cryptocurrency'].includes(formik.values.paymentInfo.preferredMethod) && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Digital Payment Details
                </Typography>
              </Divider>
            </Grid>
            
            {formik.values.paymentInfo.preferredMethod === 'instapay' && (
              <Grid item xs={12} md={6}>
                <TextField
                  label="InstaPay Number"
                  name="paymentInfo.digitalPayment.instapayNumber"
                  value={formik.values.paymentInfo.digitalPayment.instapayNumber}
                  onChange={handleChange('paymentInfo.digitalPayment.instapayNumber')}
                  onBlur={formik.handleBlur}
                />
              </Grid>
            )}
            
            {formik.values.paymentInfo.preferredMethod === 'paypal' && (
              <Grid item xs={12} md={6}>
                <TextField
                  label="PayPal Email"
                  name="paymentInfo.digitalPayment.paypalEmail"
                  value={formik.values.paymentInfo.digitalPayment.paypalEmail}
                  onChange={handleChange('paymentInfo.digitalPayment.paypalEmail')}
                  onBlur={formik.handleBlur}
                  type="email"
                />
              </Grid>
            )}
            
            {formik.values.paymentInfo.preferredMethod === 'cryptocurrency' && (
              <Grid item xs={12} md={6}>
                <TextField
                  label="Crypto Wallet Address"
                  name="paymentInfo.digitalPayment.cryptoWallet"
                  value={formik.values.paymentInfo.digitalPayment.cryptoWallet}
                  onChange={handleChange('paymentInfo.digitalPayment.cryptoWallet')}
                  onBlur={formik.handleBlur}
                />
              </Grid>
            )}
            
            {formik.values.paymentInfo.preferredMethod === 'other' && (
              <Grid item xs={12}>
                <TextField
                  label="Other Payment Details"
                  name="paymentInfo.digitalPayment.otherDetails"
                  value={formik.values.paymentInfo.digitalPayment.otherDetails}
                  onChange={handleChange('paymentInfo.digitalPayment.otherDetails')}
                  onBlur={formik.handleBlur}
                  multiline
                  rows={3}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
    </FormSection>
  );
};

export default VendorPaymentForm;